import type {
  DocumentDiffToVersionDiffOptions,
  Kind,
  VersionDiff,
  VersionTick,
} from "@norkron/web-review-workbench";
import type { Section, SectionVersion } from "../api/articleUnits";

/**
 * `versionCompare` — the PURE, product-side producers behind the W4 "pick any two
 * versions" compare (design §2.7 timeline / compare / SideBySideDiff). Kept OUT of
 * `<ArticleWorkbench>` so its surface stays small, and separate from the
 * `@norkron/web-review-workbench` lib because these are ELABORATE presentation
 * concerns (the `§N heading` label, the `v{seq}` labels): the lib already owns the
 * GENERIC machinery — the A→B `<SideBySideDiff>`, its collapsible-section grouping
 * (`hunk.sectionId`), the timeline pin-A/pin-B gestures, and the
 * `documentDiffToVersionDiff` bridge. W4 adds ZERO lib code / ZERO routes; these
 * helpers only shape the lib's inputs. All functions are pure + total (no
 * fetching, no DOM, no mutation) so they are unit-testable in isolation.
 */

/** The "§N heading" label a section renders in the workbench (matches
 *  `<SectionVersionChips>`): the 1-based ordinal + heading, or "Lead" for the
 *  pre-heading section. This string becomes the `<SideBySideDiff>` collapsible
 *  section-group HEADER, so a section-scoped diff renders under it (not flat). */
export function sectionHeaderLabel(
  section: Pick<Section, "ord" | "heading">,
): string {
  return `§${section.ord + 1} ${section.heading || "Lead"}`;
}

/** The A→B `resourceKind` + labels a compare produces, from the two PICKED ticks.
 *  `aLabel`/`bLabel` are the human `v{seq}` the tray + `<SideBySideDiff>` header
 *  show — always the seqs the reviewer chose (never head↔prev). */
export function compareOpts(
  kind: Kind,
  from: Pick<VersionTick, "seq">,
  to: Pick<VersionTick, "seq">,
): DocumentDiffToVersionDiffOptions {
  return { resourceKind: kind, aLabel: `v${from.seq}`, bLabel: `v${to.seq}` };
}

/** Tag EVERY hunk of a produced `VersionDiff` with one `sectionId`, so
 *  `<SideBySideDiff>` collapses them under a single COLLAPSIBLE section header
 *  (design §2.7 section-grained diff). Pure: returns a NEW model + new hunks,
 *  never mutates the input. The lib owns the grouping mechanism (an opaque
 *  `hunk.sectionId`); this is the elaborate-side label producer. */
export function tagSectionGroup(
  model: VersionDiff,
  sectionId: string,
): VersionDiff {
  return {
    ...model,
    hunks: model.hunks.map((h) => ({ ...h, sectionId })),
  };
}

/** Resolve a tick by its `versionId` within a tick list (any order). */
export function resolveTick(
  ticks: readonly VersionTick[],
  versionId: string | undefined,
): VersionTick | undefined {
  if (versionId === undefined) return undefined;
  return ticks.find((t) => t.versionId === versionId);
}

/** Lower a section's per-section version stream to timeline `VersionTick`s
 *  (ascending `seq`, head = newest), so the section compare tray renders the SAME
 *  picker the whole-resource tray uses. */
export function sectionVersionsToTicks(
  versions: readonly SectionVersion[],
): VersionTick[] {
  const asc = [...versions].sort((a, b) => a.seq - b.seq);
  return asc.map((v, i) => ({
    versionId: v.id,
    seq: v.seq,
    label: `v${v.seq}`,
    isHead: i === asc.length - 1,
    createdAt: v.created_at,
    // Surface the section-version editor for the in-header switcher (design §5).
    author: v.editor_name || undefined,
  }));
}

/**
 * Seed a tray's default A/B pins from a tick list. `"adjacent"` pins A=second-
 * newest, B=newest (reproduces the old head↔prev whole-resource default, now just
 * the tray's INITIAL state — still fully re-pinnable). `"span"` pins A=oldest,
 * B=newest ("seed oldest→newest" for a section tray, so the section's whole
 * evolution shows by default). A single-version list pins A=B=that version.
 */
export function seedAB(
  ticks: readonly VersionTick[],
  mode: "adjacent" | "span",
): { a?: string; b?: string } {
  if (ticks.length === 0) return {};
  const asc = [...ticks].sort((a, b) => a.seq - b.seq);
  const b = asc[asc.length - 1].versionId;
  if (asc.length === 1) return { a: asc[0].versionId, b };
  const a = mode === "span" ? asc[0].versionId : asc[asc.length - 2].versionId;
  return { a, b };
}
